import React, { useRef, useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LockResetIcon from '@mui/icons-material/LockReset';
import EditIcon from '@mui/icons-material/Edit';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { HeaderTitle, LoaderWrapper } from '../../../services/settings/appStyle/global.style';
import { Loader } from '../../../Components/uielements';
import { IntlMessages } from '../../../helper/langHelper';
import { useUserContext } from '../../../hooks/UserContext';
import Subscription, { managePlan } from '../../../services/bioplus-services/subscription';
import { EditCompanyModal } from './modals';
import AccountDeleteConfirmation from './modals/accountDeleteConfirmationModal';
import {
  StyledContainer,
  CompanyContainer,
  SectionContainer,
  SectionLabel,
  InfoGrid,
  InfoItem,
  LabelTitle,
  Value,
  ActionsWrapper,
  ActionsButton,
  ActionsMenu,
  ActionsMenuItem,
} from './company.style';
import { getPointsOfInterestList } from '../../../services/bioplus-services/pointsOfInterestList';
import ResetPasswordModal from './modals/Resetpasswordmodal';
import AccountTransferModal from './modals/accountTransferModal';
import UpdateContactDetails from './modals/updateContactDetails';

export function transformPointsOfInterestData(data) {
  if (data) {
    return data?.map((item) => ({
      value: item?._id,
      label: item?.name,
    }));
  } else {
    return [];
  }
}

function matchObjects(dataArray, idsArray) {
  if (!dataArray || !Array.isArray(dataArray) || !idsArray || !Array.isArray(idsArray)) {
    return [];
  }

  return dataArray.filter((obj) => obj && idsArray.includes(obj._id || obj.id));
}

const ERROR_SUBSCRIPTION_RETRIEVAL = 'Unable to retrieve the subscription details';

const environment = process.env.REACT_APP_ENV;

const OwnerCompany = () => {
  const [editCompany, setEditCompany] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [accountDeleteConfirmation, setAccountDeleteConfirmation] = useState(false);
  const [accountTransferConfirmation, setAccountTransferConfirmation] = useState(false);
  const [resetPassword, setResetPassword] = useState(false);
  const [updateContactDetails, setUpdateContactDetails] = useState(false);
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  const actionsRef = useRef(null);

  const userContext = useUserContext();
  const userRole = userContext?.company;
  const companyId = userRole?.companyId?._id;

  const getInterestList = useQuery(['getInterestList'], () => getPointsOfInterestList());

  const subscriptionQuery = useQuery(['subscription', companyId], () =>
    Subscription.getSubscription(companyId)
  );

  if (subscriptionQuery.isError && errorMessage !== ERROR_SUBSCRIPTION_RETRIEVAL) {
    setErrorMessage(ERROR_SUBSCRIPTION_RETRIEVAL);
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionsRef.current && !actionsRef.current.contains(event.target)) {
        setIsActionsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleClosed = () => setEditCompany(false);
  const handleResetPassword = () => setResetPassword(true);

  const handleManageSubscription = async () => {
    try {
      const response = await managePlan();
      if (response) window.open(response, '_blank');
    } catch (error) {
      alert(error.message);
    }
  };

  const handleDeleteAccountConfirmation = () => setAccountDeleteConfirmation(true);
  const handleTransferAccountConfirmation = () => setAccountTransferConfirmation(true);
  const handleUpdateContactDetails = () => setUpdateContactDetails(true);

  const subscription = subscriptionQuery.data;
  const isLoading = subscriptionQuery.isLoading;

  const matchPointsOfInterest = matchObjects(
    getInterestList?.data,
    subscription?.userData?.interestList
  );

  const showManageSubscription =
    subscription?.subscriptionData?.planType !== 'OFFLINE' && subscription?.subscriptionData?.name;

  const actionItems = [
    {
      id: 'update-profile',
      label: <IntlMessages id="button.text.profile" />,
      icon: <EditIcon fontSize="small" />,
      onClick: () => setEditCompany(true),
    },
    {
      id: 'update-contact-details',
      label: 'Update Contact Details',
      icon: <ContactPhoneIcon fontSize="small" />,
      onClick: handleUpdateContactDetails,
    },
    {
      id: 'change-password',
      label: <IntlMessages id="button.text.reset.password" />,
      icon: <LockResetIcon fontSize="small" />,
      onClick: handleResetPassword,
    },
    ...(showManageSubscription
      ? [
          {
            id: 'manage-subscription',
            label: <IntlMessages id="manage.subscription" />,
            icon: <ManageAccountsIcon fontSize="small" />,
            onClick: handleManageSubscription,
          },
        ]
      : []),
    {
      id: 'transfer-account',
      label: <IntlMessages id="button.text.transfer.account" />,
      icon: <WarningAmberIcon fontSize="small" />,
      onClick: handleTransferAccountConfirmation,
      isWarning: true,
    },
    {
      id: 'delete-account',
      label: <IntlMessages id="button.text.delete.account" />,
      icon: <WarningAmberIcon fontSize="small" />,
      onClick: handleDeleteAccountConfirmation,
      isWarning: true,
    },
  ];

  const handleActionClick = (action) => {
    setIsActionsOpen(false);
    action.onClick();
  };

  const renderInfoItem = (
    labelId,
    value,
    defaultValue = 'N/A',
    isCapitalize = true,
    isArray = false
  ) => {
    return (
      <InfoItem>
        <LabelTitle>
          <IntlMessages id={labelId} /> :
        </LabelTitle>
        <Value isCapitalize={isCapitalize} isArray={isArray}>
          {isLoading
            ? 'Loading...'
            : isArray
            ? Array.isArray(value) && value.length > 0
              ? value
                  .map((item) =>
                    typeof item === 'object' ? item.label || item.name || item : item
                  )
                  .join(', ')
              : 'No interests selected.'
            : value ?? defaultValue}
        </Value>
      </InfoItem>
    );
  };

  if (subscriptionQuery?.isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  return (
    <StyledContainer className="hide-scroll-bar">
      <CompanyContainer>
        <HeaderTitle>
          <IntlMessages id="title.text.user.profile" />
        </HeaderTitle>

        <ActionsWrapper ref={actionsRef}>
          <ActionsButton
            type="button"
            onClick={() => setIsActionsOpen((prev) => !prev)}
            isOpen={isActionsOpen}
          >
            <IntlMessages id="button.text.actions">{(label) => label || 'Actions'}</IntlMessages>
            <KeyboardArrowDownIcon fontSize="small" className="actions-arrow" />
          </ActionsButton>

          {isActionsOpen && (
            <ActionsMenu>
              {actionItems.map((action) => (
                <ActionsMenuItem
                  key={action.id}
                  type="button"
                  onClick={() => handleActionClick(action)}
                  isWarning={action.isWarning}
                >
                  {action.icon}
                  <span>{action.label}</span>
                </ActionsMenuItem>
              ))}
            </ActionsMenu>
          )}
        </ActionsWrapper>
      </CompanyContainer>

      <SectionContainer>
        <SectionLabel>Member Information</SectionLabel>
        <InfoGrid>
          {renderInfoItem(
            'company.modal.user.name',
            `${subscription?.userData?.firstName} ${subscription?.userData?.lastName}`
          )}
          {renderInfoItem(
            'label.preferedName',
            subscription?.userData?.preferedName,
            <IntlMessages id="label.not.provided" />
          )}
          {renderInfoItem(
            'input.label.company.name',
            subscription?.companyData?.name,
            <IntlMessages id="label.not.provided" />
          )}
          {renderInfoItem(
            'Business Number',
            subscription?.companyData?.abn,
            <IntlMessages id="label.not.provided" />
          )}
          {renderInfoItem(
            'label.street',
            subscription?.userData?.addressStreet,
            <IntlMessages id="label.not.provided" />
          )}
          {renderInfoItem(
            'label.country',
            subscription?.userData?.addressCountry,
            <IntlMessages id="label.not.provided" />
          )}{' '}
          {renderInfoItem(
            'label.zipcode',
            subscription?.userData?.zipCode,
            <IntlMessages id="label.not.provided" />
          )}
          {renderInfoItem(
            'label.town',
            subscription?.userData?.addressTown,
            <IntlMessages id="label.not.provided" />
          )}
          {renderInfoItem(
            'label.state',
            subscription?.userData?.addressState,
            <IntlMessages id="label.not.provided" />
          )}
          {renderInfoItem(
            'table.col.email',
            subscription?.userData?.email,
            <IntlMessages id="label.not.provided" />,
            false
          )}
          {renderInfoItem(
            'input.label.phone.no',
            subscription?.userData?.phone,
            <IntlMessages id="label.not.provided" />
          )}
        </InfoGrid>
      </SectionContainer>

      <SectionContainer>
        <SectionLabel>Subscription Details</SectionLabel>
        <InfoGrid>
          {renderInfoItem('label.subscription', subscription?.subscriptionData?.name, 'VISITOR')}
          {renderInfoItem(
            'label.subscriptionPlanCost',
            `$ ${subscription?.subscriptionData?.subscriptionId?.baseCost ?? 0}`
          )}
          {renderInfoItem('label.billingFrequency', subscription?.subscriptionData?.billingPeriod)}
        </InfoGrid>
      </SectionContainer>

      <SectionContainer>
        <SectionLabel>Personal Information</SectionLabel>
        <InfoGrid>
          {renderInfoItem('input.label.birth.date', subscription?.userData?.dateOfBirth)}
          {renderInfoItem('label.nationality', subscription?.userData?.nationality)}
          {renderInfoItem('label.driversLicence', subscription?.userData?.driversLicence)}
          {renderInfoItem('label.licenceType', subscription?.userData?.licenceType)}
          {renderInfoItem('label.renewalDateDrivers', subscription?.userData?.renewalDateDrivers)}
          {renderInfoItem('label.passportDetails', subscription?.userData?.passportDetails)}
          {renderInfoItem('label.renewalDatePassport', subscription?.userData?.renewalDatePassport)}
        </InfoGrid>
      </SectionContainer>

      <SectionContainer>
        <SectionLabel>Points of Interest</SectionLabel>
        <InfoGrid>
          {renderInfoItem('input.label.of.interest', matchPointsOfInterest, 'N/A', true, true)}
        </InfoGrid>
      </SectionContainer>

      <SectionContainer>
        <SectionLabel>Health Information</SectionLabel>
        <InfoGrid>
          {renderInfoItem('label.healthProvider', subscription?.userData?.healthProvider)}
          {renderInfoItem('label.bloodType', subscription?.userData?.bloodType)}
          {renderInfoItem('label.contactNumber', subscription?.userData?.contactNumber)}
        </InfoGrid>
      </SectionContainer>

      <SectionContainer>
        <SectionLabel>Emergency Contacts</SectionLabel>
        <InfoGrid>
          {renderInfoItem('label.nextOfKin', subscription?.userData?.nextOfKin)}
          {renderInfoItem('label.nextOfKinContact', subscription?.userData?.nextOfKinContact)}
        </InfoGrid>
      </SectionContainer>

      {editCompany && subscription !== undefined && (
        <EditCompanyModal
          onClose={handleClosed}
          data={subscription}
          companyId={companyId}
          getInterestList={getInterestList}
          matchPointsOfInterest={matchPointsOfInterest}
        />
      )}

      {updateContactDetails && subscription !== undefined && (
        <UpdateContactDetails onClose={() => setUpdateContactDetails(false)} data={subscription} />
      )}

      {accountDeleteConfirmation && (
        <AccountDeleteConfirmation onClose={() => setAccountDeleteConfirmation(false)} />
      )}
      {resetPassword && <ResetPasswordModal onClose={() => setResetPassword(false)} />}
      {accountTransferConfirmation && (
        <AccountTransferModal
          onClose={() => setAccountTransferConfirmation(false)}
          environment={environment}
        />
      )}
    </StyledContainer>
  );
};

export default OwnerCompany;
